import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { makeStyles } from '@mui/styles'
import { useRedirect, Notification, useLocaleState, useAuthProvider } from 'react-admin'
import { selectedGroup, setUserData } from '@reducer/profile'
import { setPermissionVersion } from '@store/reducers/auth'
import Grid from '@mui/material/Grid'
import Hidden from '@mui/material/Hidden'
import { getAuthToken } from '@common/auth'
import { getAuthErrorMessage } from '@common/authErrorMessages'
import { ACTIVATE_COMPANY_LOGO_WHITE } from '@/global'
import LoginForm from '@component/Auth/LoginForm'
import { loadBrand } from '@api'
import useSetState from '@hooks/useSetState'
import CONFIG from '@constant/config'
import Images from '@assets/images'

const useStyles = makeStyles(() => ({
  leftBg: {
    background: 'transparent linear-gradient(132deg, #4268B3 0%, #1B365D 100%) 0% 0% no-repeat padding-box',
    justifyContent: 'center',
  },
  main: {
    minHeight: '100vh',
  },
  branding: {
    width: '100%',
    padding: '30px 0',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
  },
  leftPanel: {
    display: 'flex',
    minHeight: '100vh',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightForm: {
    padding: '172px 135px 0',
  },
  bg: {
    position: 'absolute',
    objectFit: 'cover',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    height: '100%',
  },
  activate: {
    zIndex: 100,
    height: 32,
    marginTop: 48,
  },
}))

interface BrandingITF {
  LoginLogo?: string
  DefaultLogo?: string
  EnableDefaultLogo?: boolean
  LoginKey?: string
  LoginBackground?: string
}
const Login = () => {
  const classes = useStyles()
  const redirectTo = useRedirect()
  const dispatch = useDispatch()
  const location = useLocation()
  const { loginKey } = useParams<Record<string, string>>()
  const authProvider = useAuthProvider()
  const { nextPathname, redirectToNextPath } = useMemo(() => location.state || {}, [location.state])
  const [locale] = useLocaleState()
  const [loading, setLoading] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [{ branding, loadingBranding }, setBranding] = useSetState<{
    branding?: BrandingITF
    loadingBranding: boolean
  }>({ loadingBranding: false })
  const { DefaultLogo, LoginKey, LoginBackground, EnableDefaultLogo, LoginLogo } = branding || {}
  useEffect(() => {
    const accessToken = getAuthToken()
    if (accessToken) {
      redirectTo('/')
    }
  }, [redirectTo])
  useEffect(() => {
    if (!loginKey) return
    setBranding({ loadingBranding: true })
    loadBrand<BrandingITF>({ key: loginKey }).then((res) => {
      setBranding({ branding: res, loadingBranding: false })
    })
  }, [loginKey])
  const handleSubmit = useCallback(
    (auth) => {
      const { password, checked } = auth
      // UserName matching is case-insensitive on the backend; normalizing here
      // keeps the stored "remember me" value and any future exact-match
      // consumers consistent.
      const email = (auth.email || '').trim().toLowerCase()
      setErrorText('')
      setLoading(true)
      authProvider
        .login({ username: email, password: password })
        .then((user) => {
          // Remember the EMAIL only, and only once the login succeeded — the
          // password itself is never persisted (a stored copy goes stale after
          // any reset and the masked prefill re-submits it, specs#3346).
          if (checked) {
            localStorage.setItem('user', email)
          } else {
            localStorage.removeItem('user')
          }
          localStorage.removeItem('password')
          const { UserContext = {} } = user
          dispatch(setUserData(user))
          if (typeof UserContext?.PermissionVersion === 'number') {
            dispatch(setPermissionVersion(UserContext.PermissionVersion))
          }
          const {
            GlobalManageCustomers = false,
            ManageEntities = false,
            ManageGroups = false,
            ManageMarketing = false,
          } = UserContext?.PermissionsHash || {}
          const memberRole: string = UserContext?.MemberRoleInGroup || ''
          // PortfolioTeam lives top-level on the login response (from User.PortfolioTeam),
          // not inside PermissionsHash — same source authProvider lifts into permissions.
          const portfolioFullAccess =
            !!user?.PortfolioTeam && ['GMPM', 'OffsitePM', 'ChiefEngineer', 'Admin', 'Manager'].includes(memberRole)
          const isGlobalManageCustomers = GlobalManageCustomers
          const isSurveyWelcome = nextPathname?.includes('surveyWelcome')
          if (!isGlobalManageCustomers || isSurveyWelcome) {
            dispatch(
              selectedGroup({
                groupId: UserContext?.CurrentGroupId,
                groupName: UserContext?.CurrentGroupName?.[locale],
                group: {
                  acId: UserContext?.CurrentGroupId,
                  GroupName: UserContext?.CurrentGroupName,
                  LogoFilename: UserContext?.CurrentGroupLogoFilename,
                },
              })
            )
          }
          if (isSurveyWelcome || (redirectToNextPath && nextPathname)) {
            redirectTo(nextPathname)
          } else if (isGlobalManageCustomers) {
            redirectTo('/')
          } else {
            if (ManageEntities) {
              return redirectTo('/properties')
            }
            if (ManageGroups || portfolioFullAccess) {
              return redirectTo('/accountCreate')
            }
            if (ManageMarketing) {
              return redirectTo('/marketing/customerPortal')
            }
            redirectTo('/')
          }
        })
        .catch((error) => {
          // The interceptor is silenced for AdminLogin (ignoreUrl); the login
          // page owns its error display, always as friendly copy — never a
          // raw backend code.
          setErrorText(getAuthErrorMessage(error))
          setLoading(false)
        })
    },
    [authProvider, dispatch, locale, nextPathname, redirectTo, redirectToNextPath]
  )

  const onForgot = useCallback(() => {
    redirectTo('/reset')
  }, [redirectTo])
  const onInputChange = useCallback(() => {
    setErrorText('')
  }, [])
  if (loadingBranding) return <CircularProgress />
  if (LoginKey) {
    const logo = LoginLogo || (EnableDefaultLogo ? DefaultLogo : '')
    return (
      <Box className={classes.branding}>
        {!!logo && <img src={logo} style={{ height: '80px', zIndex: 100, marginBottom: '70px' }} />}
        <img src={LoginBackground || Images.loginBackground} className={classes.bg} />
        <LoginForm
          disabled={loading}
          errorText={errorText}
          onInputChange={onInputChange}
          onSubmit={handleSubmit}
          onForgot={onForgot}
          isCard
        />
        <img src={CONFIG.logoSvg} alt="logo" className={classes.activate} />
        <Typography sx={{ color: '#707070', zIndex: 100, marginTop: '12px' }}>Powered by Activate</Typography>
        <Notification />
      </Box>
    )
  }
  return (
    <Grid container className={classes.main}>
      <Hidden only={['xs', 'sm']}>
        <Grid item xs={6} className={classes.leftBg}>
          <Box className={classes.leftPanel}>
            <img id="loginicon" alt="loginlogo" src={ACTIVATE_COMPANY_LOGO_WHITE} width={600} />
          </Box>
        </Grid>
      </Hidden>
      <Grid item xs={12} md={6} className={classes.rightForm}>
        <LoginForm
          disabled={loading}
          errorText={errorText}
          onInputChange={onInputChange}
          onSubmit={handleSubmit}
          onForgot={onForgot}
        />
      </Grid>
      <Notification />
    </Grid>
  )
}

export default Login
